const TOKEN_KEY = "realpanel_token";
const USER_KEY = "realpanel_user_cache";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }
}

/** Instant paint: restore last known user while /auth/me refreshes */
export function getCachedUser<T = unknown>(): T | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function setCachedUser(user: unknown | null): void {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {
    /* quota / private mode */
  }
}

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

export async function api<T = unknown>(
  path: string,
  opts: RequestInit = {}
): Promise<T> {
  const headers = new Headers(opts.headers || {});
  if (!headers.has("Content-Type") && opts.body) {
    headers.set("Content-Type", "application/json");
  }
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(path, { ...opts, headers });
  const text = await res.text();
  let data: { error?: { message?: string; code?: string } } = {};
  if (text) {
    try {
      data = JSON.parse(text) as {
        error?: { message?: string; code?: string };
      };
    } catch {
      if (/^\s*</.test(text) || /doctype/i.test(text)) {
        throw new ApiError(
          `Server returned HTML instead of JSON for ${path} (HTTP ${res.status}). Check Servers → agent URL is http://127.0.0.1:7420 and agent is running.`,
          "BAD_JSON",
          res.status
        );
      }
      throw new ApiError(
        `Invalid JSON from ${path} (HTTP ${res.status})`,
        "BAD_JSON",
        res.status
      );
    }
  }
  if (!res.ok) {
    throw new ApiError(
      data?.error?.message || `HTTP ${res.status}`,
      data?.error?.code || "ERROR",
      res.status
    );
  }
  return data as T;
}
